import React, { useState, useEffect, useRef, Component } from 'react';
import { content } from './content';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import SectionNav from './components/SectionNav';

const {
  FiCheckCircle,
  FiBarChart2,
  FiTarget,
  FiUser,
  FiInfo,
  FiAlertCircle,
  FiTrendingUp,
  FiMaximize2,
  FiX,
  FiLock,
  FiChevronDown,
  FiChevronUp,
  FiMenu,
  FiExternalLink,
  FiClock,
  FiActivity,
  FiHelpCircle
} = FiIcons;

const ZIEL_HASH = "d59a23c3feff6c21bbd651244d14c5639d3aa704751d4ce7aaa481712a18456d";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="bg-white p-10 rounded-[40px] shadow-xl text-center space-y-6 max-w-md border border-red-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto">
              <SafeIcon icon={FiAlertCircle} size={32} />
            </div>
            <h2 className="text-2xl font-bold text-navy-900">Ups, etwas ist schiefgelaufen.</h2>
            <p className="text-gray-500 font-light">Die Seite konnte nicht korrekt geladen werden. Bitte laden Sie die Seite neu.</p>
            <button onClick={() => window.location.reload()} className="bg-navy-900 text-white px-8 py-3 rounded-2xl font-bold hover:bg-navy-800 transition-all">Neu laden</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Badge = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'bg-gray-100 text-gray-500 border-gray-200',
    success: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    concept: 'bg-blue-50 text-blue-600 border-blue-100',
    prototype: 'bg-purple-50 text-purple-600 border-purple-100'
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[9px] font-extrabold tracking-wider uppercase rounded-md border ml-2 align-middle transition-colors duration-300 ${styles[variant]}`}>
      {children}
    </span>
  );
};

const SectionHeader = ({ title, intro }) => (
  <div className="border-l-4 border-teal-500 pl-6 mb-8">
    <h2 className="text-3xl font-bold text-navy-900">{title}</h2>
    {intro && <p className="mt-2 text-gray-500 font-light leading-relaxed max-w-2xl">{intro}</p>}
  </div>
);

const ExpandableDetails = ({ label, text, t, status }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-bold text-xs uppercase tracking-wider transition-colors focus:outline-none rounded py-1 text-left">
          <SafeIcon icon={isOpen ? FiChevronUp : FiChevronDown} size={14} />
          <span>{label}</span>
        </button>
        {status && (
          <Badge variant={status === 'conceptOnly' ? 'concept' : 'prototype'}>
            {status === 'conceptOnly' ? t?.common?.conceptOnly : t?.common?.inPrototype}
          </Badge>
        )}
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="py-3 text-[13px] leading-relaxed text-gray-500 font-light whitespace-pre-line border-l-2 border-teal-50 pl-4 ml-1">
              {text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ChurnImpactChart = ({ t }) => {
  const section = t?.part3?.economicImpactSection?.churnImpactSection;
  if (!section) return null;
  return (
    <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-sm space-y-8">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h4 className="font-bold text-navy-900 text-lg flex items-center gap-2 uppercase tracking-tight">
          <SafeIcon icon={FiTarget} className="text-teal-600" />
          {section.title}
        </h4>
        <Badge variant="default">{t?.common?.assumption}</Badge>
      </div>
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <p className="text-gray-500 text-[13px] leading-relaxed font-light">
            {section.text}
          </p>
          <p className="text-[11px] text-gray-400 font-light leading-relaxed italic border-t border-gray-50 pt-6">
            {section.note}
          </p>
        </div>
        <div className="space-y-6 bg-gray-50 p-8 rounded-3xl border border-gray-100">
          {section.data.map((item, i) => (
            <div key={i} className="space-y-3">
              <div className="flex justify-between text-xs font-bold">
                <span className="text-navy-800 uppercase tracking-widest">{item.label}</span>
                <span className="text-teal-600 font-extrabold">{item.display}</span>
              </div>
              <div className="h-4 w-full bg-white rounded-full overflow-hidden border border-gray-100">
                <motion.div initial={{ width: 0 }} whileInView={{ width: `${item.value}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: i * 0.2, ease: "easeOut" }} className={`h-full rounded-full ${i === 0 ? 'bg-gray-300' : i === 1 ? 'bg-teal-400' : 'bg-teal-600'}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RampChart = ({ t }) => {
  const data = [
    { label: 'T14', base: 3, sol1: 5, sol2: 5 },
    { label: 'T30', base: 5, sol1: 8, sol2: 8 },
    { label: 'T90', base: 8, sol1: 10, sol2: 11 },
    { label: 'T180', base: 10, sol1: 11, sol2: 12 },
    { label: 'T360', base: 10, sol1: 11, sol2: 12 },
  ];
  const maxValue = 13;
  const height = 200;
  const section = t?.part3?.economicImpactSection;
  const getPoints = (key) => {
    return data.map((d, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = height - (d[key] / maxValue) * height;
      return `${x},${y}`;
    }).join(' ');
  };

  return (
    <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm space-y-8 flex flex-col h-full">
      <h4 className="font-bold text-navy-900 text-lg flex items-center gap-2 uppercase tracking-tight">
        <SafeIcon icon={FiTrendingUp} className="text-teal-600" />
        {section?.rampTitle || "Ramp Curves"}
      </h4>
      <div className="relative h-[240px] w-full pt-10 px-4 flex-1">
        <div className="absolute left-0 top-10 h-[200px] flex flex-col justify-between text-[10px] text-gray-400 font-bold">
          <span>12</span>
          <span>8</span>
          <span>4</span>
          <span>0</span>
        </div>
        <div className="ml-8 h-[200px] border-l border-b border-gray-100 relative flex items-end justify-between px-4">
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3].map((_, i) => (
              <div key={i} className="w-full border-t border-gray-50" style={{ height: '1px' }} />
            ))}
          </div>
          <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none" viewBox={`0 0 100 ${height}`}>
            <polyline fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4,4" points={getPoints('base')} vectorEffect="non-scaling-stroke" />
            <polyline fill="none" stroke="#14b8a6" strokeWidth="2.5" points={getPoints('sol1')} vectorEffect="non-scaling-stroke" />
            <polyline fill="none" stroke="#0f766e" strokeWidth="2.5" points={getPoints('sol2')} vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="absolute inset-0 w-full h-full pointer-events-none">
            {data.map((d, i) => {
              const x = (i / (data.length - 1)) * 100;
              return (
                <div key={i} className="absolute inset-0">
                  <div className="absolute w-2 h-2 bg-slate-300 rounded-full -translate-x-1/2 -translate-y-1/2 border border-white" style={{ left: `${x}%`, top: `${(1 - d.base/maxValue) * 100}%` }} />
                  <div className="absolute w-2.5 h-2.5 bg-teal-500 rounded-full -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-sm" style={{ left: `${x}%`, top: `${(1 - d.sol1/maxValue) * 100}%` }} />
                  <div className="absolute w-2.5 h-2.5 bg-teal-800 rounded-full -translate-x-1/2 -translate-y-1/2 border-2 border-white shadow-sm" style={{ left: `${x}%`, top: `${(1 - d.sol2/maxValue) * 100}%` }} />
                </div>
              );
            })}
          </div>
          {data.map((d, i) => (
            <div key={i} className="flex flex-col items-center relative z-10">
              <div className="text-[10px] font-bold text-gray-400 mt-4 absolute -bottom-8">{d.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-3 pt-12 justify-center border-t border-gray-50">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-slate-300 border-t border-dashed" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{section?.rampTableHeaders?.[1]}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-teal-500" />
          <span className="text-[10px] font-bold text-teal-600 uppercase tracking-widest">{section?.rampTableHeaders?.[2]}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-teal-800" />
          <span className="text-[10px] font-bold text-teal-900 uppercase tracking-widest">{section?.rampTableHeaders?.[3]}</span>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 font-light leading-relaxed italic pt-2">
        {section?.rampNote}
      </p>
    </div>
  );
};

function App() {
  const [lang, setLang] = useState(() => sessionStorage.getItem('language') || 'DE');
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem('isUnlocked') === 'true');
  const [pinInput, setPinInput] = useState('');
  const [hasError, setHasError] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState('B');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const t = content[lang] || content.DE;

  useEffect(() => {
    sessionStorage.setItem('language', lang);
    document.documentElement.lang = lang.toLowerCase();
    document.title = `Product Case Study - Marcus Schmitt`;
  }, [lang]);

  useEffect(() => {
    if (!isUnlocked && inputRef.current) inputRef.current.focus();
    document.body.style.overflow = (isUnlocked && !isMobileMenuOpen) ? 'auto' : 'hidden';
  }, [isUnlocked, isMobileMenuOpen]);

  const handleUnlock = async (e) => {
    e?.preventDefault();
    const daten = new TextEncoder().encode(pinInput);
    const buffer = await crypto.subtle.digest("SHA-256", daten);
    const hash = Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, "0")).join("");
    if (hash === ZIEL_HASH) {
      setIsUnlocked(true);
      sessionStorage.setItem('isUnlocked', 'true');
    } else {
      setHasError(true);
      setPinInput('');
      setTimeout(() => setHasError(false), 500);
    }
  };

  const econ = t?.part3?.economicImpactSection;

  return (
    <ErrorBoundary>
      <div className="relative min-h-screen bg-gray-50 font-sans selection:bg-teal-100 selection:text-teal-900">
        {!isUnlocked && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-navy-900/60 backdrop-blur-md p-6">
            <div className="bg-white rounded-[40px] p-8 md:p-10 shadow-2xl max-w-sm w-full text-center space-y-8 border border-white/20">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center text-white mx-auto shadow-lg shadow-teal-500/20">
                <SafeIcon icon={FiLock} size={32} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-navy-900">{t?.pinGate?.title}</h2>
                <p className="text-gray-500 text-sm font-light leading-relaxed">{t?.pinGate?.text}</p>
              </div>
              <form onSubmit={handleUnlock} className="space-y-4">
                <input ref={inputRef} type="password" inputMode="numeric" maxLength={4} placeholder={t?.pinGate?.placeholder} value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))} className={`w-full px-4 py-4 bg-gray-50 border-2 rounded-2xl text-center text-base md:text-lg font-bold placeholder:font-medium tracking-[0.3em] focus:outline-none transition-all ${hasError ? 'border-red-500 ring-4 ring-red-50' : 'border-gray-100 focus:border-teal-500 focus:ring-4 focus:ring-teal-50'}`} />
                <button type="submit" className="w-full bg-navy-900 hover:bg-navy-800 text-white font-bold py-4 rounded-2xl shadow-lg transition-all active:scale-[0.98]">{t?.pinGate?.button}</button>
              </form>
              <div className="flex justify-center bg-gray-100 rounded-xl p-1 w-fit mx-auto">
                {['DE', 'EN'].map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-4 py-1.5 text-[10px] font-bold rounded-lg transition-all ${lang === l ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={`transition-all duration-700 ${!isUnlocked ? 'blur-sm grayscale opacity-40 pointer-events-none' : ''}`}>
          <SectionNav t={t} />

          <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-[90] shadow-sm">
            <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-navy-900 rounded-lg transition-colors">
                <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} size={24} />
              </button>
              <div className="hidden md:flex space-x-8 text-xs font-bold text-gray-500 uppercase tracking-widest">
                <a href="#part1" className="hover:text-navy-900 transition-colors">{t?.nav?.part1}</a>
                <a href="#part2" className="hover:text-navy-900 transition-colors">{t?.nav?.part2}</a>
                <a href="#part3" className="hover:text-navy-900 transition-colors">{t?.nav?.part3}</a>
                <a href="#breakeven" className="hover:text-navy-900 transition-colors">{t?.nav?.breakeven}</a>
              </div>
              <div className="flex bg-gray-100 rounded-lg p-1">
                {['DE', 'EN'].map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-4 py-1 text-xs font-bold rounded-md transition-all ${lang === l ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500'}`}>{l}</button>
                ))}
              </div>
            </div>
          </header>

          {lightboxImage && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-navy-900/90 backdrop-blur-sm p-10" onClick={() => setLightboxImage(null)}>
              <img src={lightboxImage} alt="Mockup" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
            </div>
          )}

          <main className="pt-24 pb-20">
            <div className="max-w-4xl mx-auto px-6 space-y-32">
              {/* Hero & Summary Section */}
              <section id="hero" className="text-center space-y-12 pt-10 scroll-mt-32">
                <div className="space-y-4">
                  <h2 className="text-teal-600 font-bold tracking-widest uppercase text-xs">{t?.hero?.role}</h2>
                  <h1 className="text-4xl md:text-5xl font-extrabold text-navy-900 tracking-tight leading-tight">{t?.hero?.name}</h1>
                </div>
                <div className="max-w-3xl mx-auto bg-navy-900 rounded-[40px] p-10 text-left text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 blur-3xl -translate-y-1/2 translate-x-1/2 rounded-full" />
                  <h3 className="text-teal-400 font-bold uppercase tracking-widest text-xs mb-4">{t?.hero?.summaryTitle}</h3>
                  <p className="text-lg leading-relaxed font-light text-gray-100">{t?.hero?.summaryText}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left items-start align-top">
                  <div className="space-y-6">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2">{t?.hero?.assumptionsTitle}</h4>
                    <div className="space-y-3">
                      {t?.hero?.assumptions?.map((ass, i) => (
                        <div key={i} className="bg-white border border-gray-100 p-4 rounded-2xl flex items-start justify-between gap-4 shadow-sm hover:shadow-md transition-shadow">
                          <span className="text-xs text-gray-600 leading-relaxed">{ass.text}</span>
                          <Badge variant={ass.type === 'confirmed' ? 'success' : 'default'}>
                            {ass.type === 'confirmed' ? t?.common?.confirmed : t?.common?.assumption}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-100/50 rounded-3xl p-8 space-y-6 self-start">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">{lang === 'DE' ? 'LEGENDE' : 'LEGEND'}</h4>
                    <div className="space-y-4">
                      {Object.entries(t?.hero?.legend || {}).map(([key, text]) => (
                        <div key={key} className="flex gap-3 items-start">
                          <Badge variant={key === 'confirmed' ? 'success' : key === 'assumption' ? 'default' : key === 'concept' ? 'concept' : 'prototype'}>
                            {t?.common?.[key] || key.toUpperCase()}
                          </Badge>
                          <span className="text-[11px] text-gray-500 leading-snug">{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Part 1 */}
              <section id="part1" className="scroll-mt-32 space-y-12">
                <SectionHeader title={t?.part1?.title} intro={t?.part1?.intro} />
                <div className="grid md:grid-cols-2 gap-8">
                  {[
                    { title: t?.part1?.moment1Title, desc: t?.part1?.moment1Desc },
                    { title: t?.part1?.moment2Title, desc: t?.part1?.moment2Desc }
                  ].map((m, i) => (
                    <div key={i} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 relative group">
                      <div className="absolute top-6 right-8 text-teal-100 group-hover:text-teal-200 transition-colors"><SafeIcon icon={FiAlertCircle} size={24} /></div>
                      <h4 className="font-bold text-navy-900 mb-4 uppercase tracking-tight text-sm">{m.title}</h4>
                      <p className="text-gray-500 text-[13px] leading-relaxed font-light">{m.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {t?.part1?.phases?.map((phase, i) => (
                    <div key={i} className={`p-6 rounded-3xl border transition-all ${phase.highlight ? 'bg-white border-teal-500 shadow-xl ring-4 ring-teal-50' : 'bg-white border-gray-100 shadow-sm'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-bold text-gray-300">0{i + 1}</span>
                        <h4 className="font-bold text-xs uppercase text-navy-900">{phase.name}</h4>
                      </div>
                      <p className="text-red-500 text-[10px] font-extrabold uppercase tracking-tighter mb-2">{phase.friction}</p>
                      <p className="text-[11px] text-gray-500 leading-relaxed font-light">{phase.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-[40px] p-8 md:p-12 border-2 border-dashed border-gray-200 flex flex-col md:flex-row gap-10 items-center">
                  <div className="w-20 h-20 bg-teal-500 rounded-3xl text-white shadow-2xl flex items-center justify-center shrink-0"><SafeIcon icon={FiBarChart2} size={32} /></div>
                  <div className="flex-1 space-y-4">
                    <h4 className="text-xl font-bold text-navy-900">{t?.part1?.metricTitle}</h4>
                    <div className="flex items-center gap-3">
                      <span className="text-teal-600 font-extrabold text-2xl tracking-tight">{t?.part1?.metricValue}</span>
                      <Badge variant="default">{t?.common?.assumption}</Badge>
                    </div>
                    <p className="text-gray-500 text-sm font-light leading-relaxed">{t?.part1?.metricText}</p>
                    <ExpandableDetails label={t?.part1?.metricDetails?.label} text={t?.part1?.metricDetails?.text} t={t} />
                  </div>
                </div>
              </section>

              {/* Part 2 */}
              <section id="part2" className="scroll-mt-32 space-y-16">
                <SectionHeader title={t?.part2?.title} intro={t?.part2?.intro} />
                <div className="grid md:grid-cols-3 gap-6 items-stretch">
                  {t?.part2?.options?.map((opt) => (
                    <button key={opt.id} onClick={() => setSelectedOptionId(opt.id)} className={`p-8 rounded-[40px] border text-left transition-all duration-500 flex flex-col h-full relative ${selectedOptionId === opt.id ? 'bg-navy-900 text-white shadow-2xl border-navy-900 scale-[1.02] z-10' : 'bg-white border-gray-100 hover:border-teal-500 shadow-sm z-0'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold mb-6 text-sm shrink-0 ${selectedOptionId === opt.id ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-400'}`}>{opt.id}</div>
                      <h4 className="font-bold text-sm uppercase tracking-widest mb-3">{opt.title}</h4>
                      <p className={`text-[12px] leading-relaxed font-light flex-1 ${selectedOptionId === opt.id ? 'text-gray-400' : 'text-gray-500'}`}>{opt.desc}</p>
                      {opt.selected && <div className="mt-6 shrink-0"><Badge variant="success">{lang === 'DE' ? 'Fokus' : 'Focus'}</Badge></div>}
                    </button>
                  ))}
                </div>

                <div className="space-y-12">
                  <h3 className="text-xl font-bold text-navy-800 tracking-tight">{t?.part2?.solutionsTitle}</h3>
                  {t?.part2?.solutions?.map((sol, idx) => (
                    <React.Fragment key={idx}>
                      {/* Insert Learning Process between Sol 1 and Sol 2 */}
                      {idx === 1 && (
                        <div className="bg-white p-10 rounded-[40px] shadow-sm border-2 border-dashed border-gray-200 space-y-8">
                          <div className="flex items-center justify-between gap-4">
                            <h4 className="text-xl font-bold text-navy-900 uppercase tracking-tight flex items-center gap-3">
                              <SafeIcon icon={FiHelpCircle} className="text-teal-600" />
                              {t?.part2?.learningProcess?.title}
                            </h4>
                            <Badge variant="default">{t?.common?.assumption}</Badge>
                          </div>
                          
                          <div className="bg-navy-900 text-white p-8 rounded-3xl border border-white/10 space-y-4">
                            <p className="text-[13px] leading-relaxed font-light text-gray-300 whitespace-pre-line">
                              {t?.part2?.learningProcess?.importantNote}
                            </p>
                          </div>

                          <p className="text-gray-500 text-sm leading-relaxed font-light">{t?.part2?.learningProcess?.intro}</p>
                          
                          <div className="grid md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                              <h5 className="font-bold text-navy-900 text-sm uppercase tracking-widest">{t?.part2?.learningProcess?.whyNotTranscriptTitle}</h5>
                              <p className="text-gray-500 text-[12px] leading-relaxed font-light">{t?.part2?.learningProcess?.whyNotTranscriptText}</p>
                            </div>
                            <div className="space-y-4">
                              <h5 className="font-bold text-navy-900 text-sm uppercase tracking-widest">{t?.part2?.learningProcess?.howWeCaptureTitle}</h5>
                              <p className="text-gray-500 text-[12px] leading-relaxed font-light whitespace-pre-line">{t?.part2?.learningProcess?.howWeCaptureText}</p>
                            </div>
                          </div>
                          
                          <div className="bg-teal-50/50 p-5 rounded-2xl border border-teal-100/50">
                            <p className="text-teal-900 text-[11px] leading-relaxed font-medium italic">{t?.part2?.learningProcess?.legalNote}</p>
                          </div>
                        </div>
                      )}

                      <div className="bg-white p-10 rounded-[40px] shadow-sm border border-gray-100 grid md:grid-cols-2 gap-12 items-start">
                        <div className="space-y-6 self-start">
                          <div className="flex items-center gap-4">
                            <span className="text-[10px] font-bold text-teal-500">{sol.label}</span>
                            <h4 className="font-bold text-navy-900 text-xl">{sol.title}</h4>
                          </div>
                          <p className="text-gray-500 text-sm leading-relaxed font-light whitespace-pre-line">{sol.desc}</p>
                          
                          {sol.id === 'sol2' && (
                            <div className="mt-8 space-y-4 bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
                              <p className="text-navy-900 text-[13px] font-bold leading-relaxed">{sol.mentorQuestionsIntro}</p>
                              <ul className="space-y-3">
                                {sol.mentorQuestions?.map((q, i) => (
                                  <li key={i} className="flex gap-3 items-start">
                                    <span className="text-teal-500 font-bold shrink-0">•</span>
                                    <span className="text-gray-600 text-[12px] font-medium leading-relaxed italic">{q}</span>
                                  </li>
                                ))}
                              </ul>
                              <p className="text-gray-500 text-[11px] leading-relaxed font-light pt-2 border-t border-gray-100">{sol.mentorQuestionsOutro}</p>
                            </div>
                          )}

                          <div className="space-y-1">
                            {sol.expandables?.map((exp, i) => (
                              <ExpandableDetails key={i} label={exp.label} text={exp.text} t={t} status={exp.status} />
                            ))}
                          </div>
                        </div>
                        <div className="space-y-4 self-start">
                          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-100 group cursor-pointer aspect-video bg-gray-50 flex items-center justify-center" onClick={() => setLightboxImage(sol.image)}>
                            <img src={sol.image} alt={sol.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute top-4 right-4"><Badge variant="success">{sol.badgeText}</Badge></div>
                            <div className="absolute inset-0 bg-navy-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <SafeIcon icon={FiMaximize2} className="text-white" size={32} />
                            </div>
                          </div>
                          {sol.prototypeUrl && (
                            <a href={sol.prototypeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-bold text-xs py-4 rounded-2xl transition-all shadow-lg shadow-teal-500/20">
                              <SafeIcon icon={FiExternalLink} size={16} />
                              {sol.prototypeLinkText}
                            </a>
                          )}
                          {sol.note && (
                            <div className="bg-teal-50 border border-teal-100 p-5 rounded-2xl mt-4">
                              <p className="text-teal-900 text-[11px] leading-relaxed font-medium italic">{sol.note}</p>
                            </div>
                          )}
                          <p className="text-[10px] text-gray-400 text-center font-medium italic">{sol.caption}</p>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </section>

              <section id="part3" className="scroll-mt-32 space-y-16">
                <SectionHeader title={t?.part3?.title} intro={t?.part3?.intro} />
                <div className="grid md:grid-cols-2 gap-12 items-start">
                  <div className="space-y-8">
                    <h3 className="text-xl font-bold text-navy-800">{t?.part3?.prioTitle}</h3>
                    <div className="space-y-4">
                      {t?.part3?.prioPoints?.map((item, i) => (
                        <div key={i} className="flex gap-5 p-6 bg-white rounded-3xl border border-gray-50 shadow-sm hover:shadow-md transition-shadow">
                          <span className="shrink-0 w-10 h-10 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                          <div>
                            <h5 className="font-bold text-navy-900 text-sm mb-1">{item.t}</h5>
                            <p className="text-gray-500 text-[12px] font-light leading-relaxed">{item.d}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div id="success" className="scroll-mt-32 bg-navy-900 text-white p-10 rounded-[40px] shadow-2xl flex flex-col gap-10">
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-teal-400 flex items-center gap-3"><SafeIcon icon={FiTarget} /> {t?.part3?.activeDefTitle}</h3>
                      <p className="text-gray-300 text-sm font-light leading-relaxed">{t?.part3?.activeDefText}</p>
                    </div>
                    <div className="space-y-4 pt-8 border-t border-white/10">
                      {t?.part3?.metrics?.map((m, i) => (
                        <div key={i} className="flex flex-col gap-1.5 bg-white/5 p-5 rounded-2xl border border-white/5">
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{m.label}</span>
                          <span className="font-bold text-base text-teal-400 leading-tight">{m.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-12 pt-10">
                  <h3 className="text-2xl font-bold text-navy-900 flex items-center gap-3"><SafeIcon icon={FiTrendingUp} className="text-teal-600" /> {t?.part3?.failureTitle}</h3>
                  <div className="bg-teal-50 border border-teal-100 p-10 rounded-[40px] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-teal-500/5 blur-3xl -translate-y-1/2 translate-x-1/2 rounded-full" />
                    <h4 className="font-bold text-teal-800 text-lg mb-3 flex items-center gap-2 uppercase tracking-tight">
                      <SafeIcon icon={FiAlertCircle} size={20} />
                      {lang === 'DE' ? 'Diagnose bei ausbleibender Steigerung' : 'Diagnosis if no increase occurs'}
                    </h4>
                    <p className="text-teal-900 text-sm leading-relaxed font-medium max-w-3xl">{t?.part3?.failureDiagnosis}</p>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {t?.part3?.failureSteps?.map((step, i) => (
                      <div key={i} className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                        <div className="text-[10px] font-bold text-teal-500 mb-4">{lang === 'DE' ? 'SCHRITT' : 'STEP'} 0{i + 1}</div>
                        <h4 className="font-bold text-navy-900 mb-3 text-base">{step.t}</h4>
                        <p className="text-gray-500 text-[12px] leading-relaxed font-light">{step.d}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section id="effort" className="scroll-mt-32 space-y-16 pt-16 border-t border-gray-200">
                <SectionHeader title={t?.part3?.effortSection?.title} intro={t?.part3?.effortSection?.intro} />
                <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-sm border border-gray-100 space-y-10">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiClock} className="text-teal-600" />
                    <h4 className="text-xl font-bold text-navy-800">{t?.part3?.effortSection?.title}</h4>
                    <Badge variant="default">{t?.common?.assumption}</Badge>
                  </div>
                  <div className="overflow-x-auto rounded-3xl border border-gray-100">
                    <table className="w-full text-left border-collapse min-w-[700px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          {t?.part3?.effortSection?.headers?.map((h, i) => (
                            <th key={i} className={`py-5 px-6 text-[9px] font-bold text-navy-800 uppercase tracking-widest ${i > 1 ? 'text-right' : ''}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {t?.part3?.effortSection?.phases?.map((ph, i) => (
                          <tr key={i} className={ph.isTotal ? "bg-teal-50/50" : "hover:bg-gray-50/50 transition-colors"}>
                            <td className={`py-4 px-6 text-xs whitespace-nowrap ${ph.isTotal ? 'font-bold text-navy-900' : 'text-navy-800'}`}>{ph.p}</td>
                            <td className={`py-4 px-6 text-xs leading-relaxed ${ph.isTotal ? 'font-bold text-navy-900' : 'text-gray-600'}`}>{ph.t}</td>
                            <td className={`py-4 px-6 text-xs font-bold text-right text-gray-500`}>{ph.l1}</td>
                            <td className={`py-4 px-6 text-xs font-bold text-right text-gray-500`}>{ph.l2}</td>
                            <td className={`py-4 px-6 text-xs font-bold text-right w-24 ${ph.isTotal ? 'text-teal-600' : 'text-teal-500'}`}>{ph.s}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid md:grid-cols-2 gap-12 items-start pt-6">
                    <div className="space-y-6">
                      <h5 className="font-bold text-navy-900 text-sm uppercase tracking-widest">{lang === 'DE' ? 'Kostenschätzung' : 'Cost Estimate'}</h5>
                      <div className="overflow-hidden rounded-3xl border border-gray-100">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                              {t?.part3?.effortSection?.costHeaders?.map((h, i) => (
                                <th key={i} className={`py-4 px-6 text-[9px] font-bold text-navy-800 uppercase tracking-widest ${i > 0 ? 'text-right' : ''}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {t?.part3?.effortSection?.costs?.map((c, i) => (
                              <tr key={i} className={c.isTotal ? "bg-teal-50/50" : ""}>
                                <td className={`py-4 px-6 text-xs ${c.isTotal ? 'font-bold text-navy-900' : 'text-navy-800'}`}>{c.item}</td>
                                <td className={`py-4 px-6 text-xs text-right text-gray-500 font-medium`}>{c.hours}</td>
                                <td className={`py-4 px-6 text-xs font-bold text-right ${c.isTotal ? 'text-teal-600' : 'text-navy-900'}`}>{c.cost}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="bg-navy-900 text-white p-8 rounded-[40px] shadow-xl space-y-6 self-stretch flex flex-col justify-center">
                      <div className="flex items-start gap-3">
                        <SafeIcon icon={FiInfo} className="text-teal-400 shrink-0 mt-1" />
                        <p className="text-[13px] leading-relaxed font-light text-gray-300">
                          {t?.part3?.effortSection?.rateNote}
                        </p>
                      </div>
                      <div className="flex items-start gap-3 pt-4 border-t border-white/10">
                        <SafeIcon icon={FiClock} className="text-teal-400 shrink-0 mt-1" />
                        <p className="text-[13px] leading-relaxed font-light text-gray-300">
                          {t?.part3?.effortSection?.capacityNote}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section id="breakeven" className="scroll-mt-32 space-y-16 pt-16">
                <SectionHeader title={econ?.title || "Economic Impact"} intro={econ?.intro} />
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-sm space-y-6">
                    <h4 className="font-bold text-navy-900 text-lg flex items-center gap-2 uppercase tracking-tight">
                      <SafeIcon icon={FiAlertCircle} className="text-teal-600" />
                      {econ?.churnTitle}
                    </h4>
                    <p className="text-gray-500 text-[13px] leading-relaxed font-light">
                      {econ?.churnText}
                    </p>
                  </div>
                  <div className="bg-teal-50 border border-teal-100 p-10 rounded-[40px] space-y-6">
                    <h4 className="font-bold text-teal-800 text-lg flex items-center gap-2 uppercase tracking-tight">
                      <SafeIcon icon={FiInfo} />
                      {econ?.marginTitle}
                    </h4>
                    <p className="text-teal-900 text-[13px] leading-relaxed font-medium italic">
                      {econ?.marginText}
                    </p>
                    <p className="text-[11px] text-teal-700 leading-relaxed font-light border-t border-teal-200/50 pt-4">
                      {econ?.marginSensitivity}
                    </p>
                    <Badge variant="default">{t?.common?.assumption}</Badge>
                  </div>
                </div>
                
                <ChurnImpactChart t={t} />

                <div className="grid md:grid-cols-2 gap-8 items-stretch">
                  <RampChart t={t} />
                  <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm overflow-hidden flex flex-col justify-center h-full">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          {econ?.rampTableHeaders?.map((h, i) => (
                            <th key={i} className={`py-4 px-4 text-[9px] font-bold text-navy-800 uppercase tracking-widest ${i > 0 ? 'text-right' : ''}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {econ?.rampData?.map((row, i) => (
                          <tr key={i}>
                            <td className="py-4 px-4 text-xs font-bold text-navy-900">{row[0]}</td>
                            <td className="py-4 px-4 text-xs text-right text-gray-400">{row[1]}</td>
                            <td className="py-4 px-4 text-xs text-right text-teal-600 font-bold">{row[2]}</td>
                            <td className="py-4 px-4 text-xs text-right text-teal-800 font-extrabold">{row[3]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-sm space-y-6">
                    <h4 className="font-bold text-navy-900 text-lg flex items-center gap-2 uppercase tracking-tight">
                      <SafeIcon icon={FiBarChart2} className="text-teal-600" />
                      {econ?.marginTableTitle}
                    </h4>
                    <div className="overflow-hidden rounded-2xl border border-gray-50">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            {econ?.marginTableHeaders?.map((h, i) => (
                              <th key={i} className={`py-3 px-4 text-[9px] font-bold text-navy-800 uppercase tracking-widest ${i > 0 ? 'text-right' : ''}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {econ?.marginData?.map((row, i) => (
                            <tr key={i}>
                              <td className="py-3 px-4 text-[11px] font-bold text-navy-900">{row[0]}</td>
                              <td className="py-3 px-4 text-[11px] text-right text-teal-600">{row[1]}</td>
                              <td className="py-3 px-4 text-[11px] text-right text-teal-600">{row[2]}</td>
                              <td className="py-3 px-4 text-[11px] text-right text-teal-800 font-bold">{row[3]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-gray-400 italic">{econ?.marginTableNote}</p>
                  </div>

                  <div className="bg-navy-900 text-white p-10 rounded-[40px] shadow-2xl space-y-8">
                    <h4 className="font-bold text-teal-400 text-lg flex items-center gap-2 uppercase tracking-tight">
                      <SafeIcon icon={FiActivity} /> {econ?.cohortTitle}
                    </h4>
                    <div className="space-y-4">
                      {econ?.cohortResults?.map((res, i) => (
                        <div key={i} className={`flex justify-between items-center p-5 rounded-2xl border ${res.highlight ? 'bg-teal-500/10 border-teal-500/30' : 'bg-white/5 border-white/10'}`}>
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${res.highlight ? 'text-teal-400' : 'text-gray-400'}`}>{res.label}</span>
                          <span className={`text-base font-extrabold ${res.highlight ? 'text-teal-400' : 'text-white'}`}>{res.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-sm space-y-10">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiTarget} className="text-teal-600" />
                      <h4 className="text-xl font-bold text-navy-800 uppercase tracking-tight">{econ?.paybackTitle}</h4>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      {econ?.paybackItems?.map((item, i) => (
                        <div key={i} className={`p-6 rounded-3xl border flex flex-col gap-4 ${item.highlight ? 'bg-teal-50 border-teal-200 ring-4 ring-teal-50' : 'bg-gray-50 border-gray-100'}`}>
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${item.highlight ? 'text-teal-600' : 'text-gray-400'}`}>{item.label}</span>
                          <div className="space-y-1">
                            <div className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter">{lang === 'DE' ? 'Baukosten' : 'Build Cost'}: {item.cost}</div>
                            <div className="text-xl font-extrabold text-navy-900">{item.result}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100 space-y-4">
                      <h5 className="font-bold text-navy-900 text-sm flex items-center gap-2">
                        <SafeIcon icon={FiInfo} className="text-teal-600" /> {econ?.paybackExplanationTitle}
                      </h5>
                      <p className="text-[13px] text-gray-500 font-light leading-relaxed">
                        {econ?.paybackExplanation}
                      </p>
                    </div>
                    <p className="text-[12px] text-gray-400 font-light leading-relaxed italic">
                      {econ?.paybackNote}
                    </p>
                  </div>
                </div>
              </section>

              <section id="questions" className="scroll-mt-32 space-y-16 pt-16 border-t border-gray-200">
                <SectionHeader title={t?.questions?.title} intro={t?.questions?.intro} />
                <div className="grid md:grid-cols-2 gap-6">
                  {t?.questions?.items?.map((item, i) => (
                    <div key={i} className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col gap-6">
                      <div className="flex justify-between items-start gap-4">
                        <h4 className="font-bold text-navy-900 text-base leading-tight">{item.q}</h4>
                        <Badge variant={item.status === 'solved' ? 'success' : 'default'}>
                          {item.status === 'solved' ? t?.common?.solved : t?.common?.assumption}
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-teal-50/50 p-5 rounded-2xl border border-teal-100/50">
                          <span className="text-[9px] font-extrabold text-teal-600 uppercase tracking-widest block mb-2">{lang === 'DE' ? 'Erste Lösung' : 'First Solution'}</span>
                          <p className="text-[13px] text-navy-900 font-bold leading-relaxed">{item.s}</p>
                        </div>
                        <div className="px-1">
                          <span className="text-[9px] font-extrabold text-gray-400 uppercase tracking-widest block mb-1">{lang === 'DE' ? 'Warum' : 'Why'}</span>
                          <p className="text-[12px] text-gray-500 font-light leading-relaxed italic">{item.w}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </main>

          <footer className="bg-white border-t border-gray-200 py-32">
            <div className="max-w-4xl mx-auto px-6 text-center md:text-left">
              <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="w-28 h-28 bg-navy-100 rounded-[40px] flex items-center justify-center text-navy-600 shrink-0 transform rotate-6 shadow-xl overflow-hidden border-4 border-white">
                  <img src="https://media-manager-c.questera.ai/greta-media/7e4a9c767f1b6bc32d9ae74da3c793a469ade620e650261cdbc16bd121baf63aa043e6e0cde1a6fa76e813042dd40456/images/aW1hZ2UvanBlZw==/ec30ba3022a465ffb14c48818348efd7.jpeg" alt="Marcus Schmitt" className="w-full h-full object-cover object-center" />
                </div>
                <div className="space-y-6">
                  <h3 className="text-3xl font-bold text-navy-900 tracking-tight">{t?.footer?.title}</h3>
                  <p className="text-gray-500 text-xl font-light leading-relaxed max-w-2xl">{t?.footer?.text}</p>
                </div>
              </div>
              <div className="mt-20 pt-10 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-xs text-gray-400">© {new Date().getFullYear()} Marcus Schmitt. {t?.footer?.copyright}</p>
                <div className="flex gap-6 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  <span>TeleClinic Case Study</span>
                  <span>Confidential</span>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;